import React, { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { Input } from '../Input/Input';
import { Button } from '../Button/Button';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { PasswordValidation } from '../../validations/PasswordValidation';
import { EyeOff, EyeOn, XCloseModal } from '../../../icons/SvgIcons';
import { changePassword } from '../../../services/UserService';
import { PrimaryButton, TextButton, WhiteButton } from '../Button/Buttons';

export default function ModalChangePassword({
  setShowToast
}: {
  setShowToast: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [validPassword, setValidPassword] = useState(false);
  const [buttonText, setButtonText] = useState('Change Password');
  const match = password === passwordConfirm;

  const disableButton = !validPassword || !match || buttonText === 'Saving...';

  const handleChangePassword = async () => {
    const requestBody = {
      newPassword: password,
      confirmPassword: passwordConfirm
    };
    try {
      setButtonText('Saving...');

      await changePassword(requestBody);
      setOpen(false);
      resetForm();
      setShowToast(true);
    } catch (error: any) {
      console.error('Error changing password: ', error);
    } finally {
      setButtonText('Change Password');
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setPassword('');
    setPasswordConfirm('');
    setShowPassword(false);
    setShowPasswordConfirm(false);
  };

  return (
    <div className="flex items-start">
      <TextButton onClick={() => setOpen(true)}>Change Password</TextButton>
      <Dialog open={open} onClose={() => {}} className="relative z-10">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-[rgba(24,24,26,0.4)] transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all p-6 data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <span className="flex items-center justify-between mb-6 text-lg text-gray-950 font-medium">
                Change password
                <Button buttonClassName="" onClick={handleCloseModal}>
                  {XCloseModal()}
                </Button>
              </span>
              <div className="flex flex-col gap-6 self-stretch">
                <div className="flex flex-col gap-2">
                  <Input
                    label="New Password"
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required={true}
                    value={password}
                    placeholder="Enter new password"
                    maxLength={72}
                    preventSpacing
                    onChange={(e) => {
                      setPassword(e.target.value);
                    }}
                    containerClassName="flex flex-col items-start gap-1 self-stretch"
                    labelClassName="label"
                    inputClassName={`w-full px-[13px] py-[9px] pr-10 rounded-md text-gray-900 shadow-sm ring-1 outline-none placeholder:text-gray-550  ${
                      !match && passwordConfirm
                        ? 'ring-rose-400 text-rose-800 focus:ring-2 focus:ring-rose-400'
                        : 'hover:ring-1 hover:ring-purple-800 ring-gray-200 hover:ring-1 hover:ring-purple-800 focus:ring-2 focus:ring-purple-700 focus:shadow-[0_1px_2px_0_rgba(0,0,0,0.05),_0_0_0_1px_#4846BC] outline-none'
                    }`}
                  >
                    {' '}
                    {password && (
                      <Button
                        type="button"
                        onClick={() => {
                          setShowPassword(!showPassword);
                        }}
                        buttonClassName="absolute right-3 top-1/2 -translate-y-1/2 p-0 bg-transparent border-none"
                      >
                        {showPassword ? EyeOn() : EyeOff()}
                      </Button>
                    )}
                  </Input>
                </div>
                <div className="flex flex-col gap-2">
                  <Input
                    label="Confirm Password"
                    id="confirmPassword"
                    type={showPasswordConfirm ? 'text' : 'password'}
                    required={true}
                    value={passwordConfirm}
                    placeholder="Confirm password"
                    maxLength={72}
                    preventSpacing
                    onChange={(e) => {
                      setPasswordConfirm(e.target.value);
                    }}
                    containerClassName=""
                    labelClassName="label"
                    inputClassName={`w-full px-[13px] py-[9px] pr-10 rounded-md text-gray-900 shadow-sm ring-1 outline-none placeholder:text-gray-550  ${
                      !match && passwordConfirm
                        ? 'ring-rose-400 text-rose-800 focus:ring-2 focus:ring-rose-400'
                        : 'hover:ring-1 hover:ring-purple-800 ring-gray-200 hover:ring-1 hover:ring-purple-800 focus:ring-2 focus:ring-purple-700 focus:shadow-[0_1px_2px_0_rgba(0,0,0,0.05),_0_0_0_1px_#4846BC] outline-none'
                    }`}
                  >
                    {' '}
                    {passwordConfirm && (
                      <Button
                        type="button"
                        onClick={() => {
                          setShowPasswordConfirm(!showPasswordConfirm);
                        }}
                        buttonClassName="absolute right-3 top-1/2 -translate-y-1/2 p-0 bg-transparent border-none"
                      >
                        {showPasswordConfirm ? EyeOn() : EyeOff()}
                      </Button>
                    )}
                  </Input>

                  {!match && passwordConfirm && (
                    <ErrorMessage message={"Passwords don't match."} />
                  )}
                </div>
                <PasswordValidation
                  password={password}
                  setValidPassword={setValidPassword}
                />
                <div className="flex gap-3 items-center justify-end">
                  <WhiteButton onClick={handleCloseModal}>Cancel</WhiteButton>
                  <PrimaryButton
                    onClick={handleChangePassword}
                    disabled={disableButton}
                  >
                    {buttonText}
                  </PrimaryButton>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
